import type { Component } from 'solid-js'
import { createResource, createSignal, createMemo, Show } from 'solid-js'
import { useMRRSelection } from '../../shared/providers/mrr-selection.provider'
import { useUser } from '../../shared/providers/user-provider'
import { useCorisRevenue } from '../../shared/providers/coris/coris-revenue.provider'
import { DataType } from '../../features/revenue/domain/models/mrr'
import { getAvailableGroupings } from '../../features/revenue/domain/models/range'
import { computeRangeDates, getCurrentHighlightDate } from '../../shared/helpers/date.functions'
import { getFormatterForGrouping } from '../shared/charts/config/chart-formatters'
import MetricCard from '../shared/components/MetricCard'
import PeriodSelector from '../shared/components/PeriodSelector'
import GroupBySelector from '../shared/components/GroupBySelector'
import MRRChart from '../shared/components/MRRChart'
import UsersTable from '../shared/components/UsersTable'
import { Icon } from '@iconify-icon/solid'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonMetricCard, SkeletonChart, SkeletonTable } from '../shared/components/Skeleton'

// Prevent tree-shaking of directive
sticky

const UsersOverview: Component = () => {
  const {
    selectedPeriod,
    setSelectedPeriod,
    customDateRange,
    setCustomDateRange,
    selectedGrouping,
    setSelectedGrouping
  } = useMRRSelection()
  const { organizationSlug } = useUser()
  const [isStuck, setIsStuck] = createSignal(false)

  const { getMRR, getSubscriberIntervals } = useCorisRevenue()

  const availableGroupings = createMemo(() => getAvailableGroupings(selectedPeriod(), customDateRange()))

  const [mrrData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      const period = selectedPeriod()
      const grouping = selectedGrouping()
      const custom = customDateRange()
      const { fromDate, toDate, resolution } = computeRangeDates(period, grouping, custom)
      return { fromDate, toDate, resolution, organization, grouping }
    },
    async (params) => {
      if (!params) return null

      return await getMRR.execute(
        params.fromDate,
        params.toDate,
        params.resolution,
        DataType.Users,
        params.organization
      )
    }
  )

  const [intervalsData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      const { fromDate, toDate, resolution } = computeRangeDates(
        selectedPeriod(),
        selectedGrouping(),
        customDateRange()
      )
      return { fromDate, toDate, resolution, organization }
    },
    async (params) => {
      if (!params) return null

      return await getSubscriberIntervals.execute(
        params.fromDate,
        params.toDate,
        params.resolution,
        params.organization
      )
    }
  )

  const intervalItems = (split?: { yearly: number; monthly: number }) =>
    split
      ? // Always show both intervals — a 0 is information (e.g. all-yearly new users), not noise.
        [
          { label: 'Yearly', value: split.yearly },
          { label: 'Monthly', value: split.monthly }
        ]
      : undefined

  const existingSplit = () => {
    const intervals = intervalsData()
    if (!intervals) return undefined
    return {
      yearly: intervals.total.yearly - intervals.new.yearly + intervals.churn.yearly,
      monthly: intervals.total.monthly - intervals.new.monthly + intervals.churn.monthly
    }
  }

  const processedData = createMemo(() => {
    const raw = mrrData()
    if (!raw) return null
    return raw.data ?? null
  })

  const currentData = () => {
    const data = processedData()
    if (!data || data.length === 0) return null
    if (data.length === 1) return data[0]

    const last = data[data.length - 1]
    const totalNew = data.reduce((sum, d) => sum + d.new, 0)
    const totalChurn = data.reduce((sum, d) => sum + d.churn, 0)

    return {
      ...last,
      new: totalNew,
      churn: totalChurn,
      existing: last.total - totalNew - totalChurn
    }
  }

  const isEconomics = () => mrrData()?.dataType === DataType.Economics

  const formatCategory = createMemo(() => getFormatterForGrouping(selectedGrouping()))
  const highlightDate = createMemo(() => getCurrentHighlightDate(selectedGrouping()))

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="max-w-[1280px] mx-auto w-full flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-center gap-3">
            <PeriodSelector
              selectedPeriod={selectedPeriod()}
              onPeriodChange={setSelectedPeriod}
              customDateRange={customDateRange()}
              onCustomRangeChange={(from, to) => setCustomDateRange({ from, to })}
              showCompareSelector={false}
            />
            <GroupBySelector
              availableGroupings={availableGroupings()}
              selectedGrouping={selectedGrouping()}
              onGroupingChange={setSelectedGrouping}
            />
          </div>
        </div>
      </div>

      <div class="max-w-[1280px] mx-auto w-full flex flex-col gap-8">
        <Show
          when={!mrrData.loading && currentData()}
          fallback={
            <div class="flex sm:flex-row flex-col gap-4 items-center">
              <SkeletonMetricCard />
              <div class="h-4 w-4" />
              <SkeletonMetricCard />
              <div class="h-4 w-4" />
              <SkeletonMetricCard />
              <div class="h-4 w-4" />
              <SkeletonMetricCard />
            </div>
          }
        >
          <div class="flex sm:flex-row flex-col gap-4 items-center">
            <MetricCard
              titleKey={isEconomics() ? 'ls_churn_mrr' : 'ls_users_paid_users'}
              value={currentData()!.total}
              format={isEconomics() ? 'currency' : 'number'}
              showLineIndicator={true}
              breakdown={isEconomics() ? undefined : intervalItems(intervalsData()?.total)}
            />
            <Icon icon="heroicons:equals" class="text-[1rem]" />
            <MetricCard
              titleKey="ls_users_new"
              value={currentData()!.new}
              showSign={!isEconomics()}
              dotColor="#00D3F2"
              format={isEconomics() ? 'currency' : 'number'}
              breakdown={isEconomics() ? undefined : intervalItems(intervalsData()?.new)}
            />
            <Icon icon="heroicons:plus" class="text-[1rem]" />
            <MetricCard
              titleKey="ls_users_existing"
              value={currentData()!.existing}
              dotColor="#51A2FF"
              format={isEconomics() ? 'currency' : 'number'}
              breakdown={isEconomics() ? undefined : intervalItems(existingSplit())}
            />
            <Icon icon="heroicons:minus" class="text-[1rem]" />
            <MetricCard
              titleKey="ls_users_churn"
              value={isEconomics() ? Math.abs(currentData()!.churn) : -Math.abs(currentData()!.churn)}
              showSign={!isEconomics()}
              dotColor="#FF6467"
              format={isEconomics() ? 'currency' : 'number'}
              breakdown={isEconomics() ? undefined : intervalItems(intervalsData()?.churn)}
            />
          </div>
        </Show>

        <Show when={!mrrData.loading && processedData()} fallback={<SkeletonChart legendItems={4} />}>
          <MRRChart data={processedData()!} formatCategory={formatCategory()} />
        </Show>

        <Show when={!mrrData.loading && processedData()} fallback={<SkeletonTable columns={4} rows={5} />}>
          <UsersTable data={processedData() ?? []} formatHeader={formatCategory()} highlightMonth={highlightDate()} />
        </Show>
      </div>
    </div>
  )
}

export default UsersOverview
